import React, { useEffect, useMemo, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { BiSolidVolumeFull } from 'react-icons/bi';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { useAppSelector } from '@/app/hook';
import { useGetAISpeechTextMutation, useGetConfigurationQuery } from '@/app/services/chatbot-rtk.services';
import LoadingComponent from '@/components/common/loading/loading.component';
import PopoverWithAudio, { IPopoverWithAudioType } from '@/components/common/PopoverWithAudio';
import { PSInput } from '@/components/ui/input';
import ProBadge from '@/components/ui/ProBadge';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

import { DropDownWithImage } from './AppearanceSharedAi';
import { handleResetAppearance, handleUpdateAppearanceItem, selectAppearanceState } from './appearanceSlice';

interface VoiceResponseSettingsProps {
  permissions: any;
  i18n: any;
  formContext: any;
}

const customList = ['style', 'speed', 'emotion', 'emphasis', 'pauses'];

type ConfigType = 'template' | 'custom';

const AppearanceVoiceResponseToggle: React.FC<VoiceResponseSettingsProps> = ({ permissions, i18n, formContext }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const appearanceState = useAppSelector(selectAppearanceState);

  const { data, isLoading } = useGetConfigurationQuery({});
  const [getTextSpeech, { isLoading: isProcessing }] = useGetAISpeechTextMutation();

  const [configType, setConfigType] = useState<ConfigType>('template');
  const [inputValue, setInputValue] = useState("A, B, C, D! Here's a demo of my voice.");
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);

  // Popover voice and texts
  const voiceCommandPopData: IPopoverWithAudioType = {
    contentText: t('DashboardSettingCustomizeChat/pop-07'),
  };

  const {
    register,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = formContext;

  const voiceResponseEnabled = watch('voiceResponse');
  const customizationAllowed = permissions.settingsPage.widget.customization.voiceResponse;

  const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      handleUpdateAppearanceItem({
        key: 'voiceResponse',
        value: e.target.checked,
      })
    );
    if (!e.target.checked) {
      setValue('template', '');
      dispatch(
        handleUpdateAppearanceItem({
          key: 'template',
          value: '',
        })
      );
      customList.map(item => {
        setValue(item, '');
        dispatch(
          handleUpdateAppearanceItem({
            key: item,
            value: '',
          })
        );
      });
    }
  };

  const handleVoiceSelect = (item: { gender: string; voice: string }) => {
    setValue('assistantGender', item.gender);
    setValue('voice', item.voice);
    dispatch(handleUpdateAppearanceItem({ key: 'assistantGender', value: item.gender }));
    dispatch(handleUpdateAppearanceItem({ key: 'voice', value: item.voice }));
  };

  useEffect(() => {
    if (watch('voiceResponse')) {
      const config = permissions.settingsPage.widget.customization.advancedVoiceSettings ? 'QUALITY' : 'FAST';

      if (config != getValues('configuration')) {
        setValue('configuration', permissions.settingsPage.widget.customization.advancedVoiceSettings ? 'QUALITY' : 'FAST');
        dispatch(
          handleUpdateAppearanceItem({
            key: 'configuration',
            value: permissions.settingsPage.widget.customization.advancedVoiceSettings ? 'QUALITY' : 'FAST',
          })
        );
      }
    }
  }, [permissions, watch('voiceResponse')]);

  const templateList = useMemo(() => {
    const list: {
      voice: string;
      gender: 'male' | 'female';
      isAdvanced: boolean;
    }[] = [];

    // const advancedPermission =
    //   permissions.settingsPage.widget.customization.advancedVoiceSettings;
    const fastList = new Set(data?.configuration_voice?.FAST ?? []);

    const setVoiceList = (voices: string[] | undefined, gender: 'male' | 'female') => {
      voices?.forEach(voice => {
        list.push({
          voice,
          gender,
          isAdvanced: !fastList.has(voice),
        });
      });
    };

    setVoiceList(data?.voice?.FEMALE, 'female');
    setVoiceList(data?.voice?.MALE, 'male');

    return list;
  }, [data, permissions]);

  const handleVoiceResponseSound = () => {
    const currentVoice = watch('voice');
    if (!currentVoice) return;
    if (!inputValue) {
      toast.warn(t('DashboardSettingCustomizeChat/pop-17'));
      return;
    }
    const gender = templateList.find(x => x.voice == currentVoice)?.gender;

    const values = {
      text: inputValue,
      assistantGender: gender,
      voice: currentVoice,
      configuration: watch('configuration'),
      template: watch('template'),
      style: watch('style'),
      speed: watch('speed'),
      emotion: watch('emotion'),
      emphasis: watch('emphasis'),
      pauses: watch('pauses'),
    };
    const data = Object.fromEntries(Object.entries(values).filter(([_, value]) => value != null && value !== ''));
    getTextSpeech(data)
      .unwrap()
      .then(value => {
        if (value) {
          if (audio) {
            audio.pause();
            audio.currentTime = 0;
          }

          const newAudio = new Audio(`data:${value?.type};base64,${value?.content}`);
          setAudio(newAudio);

          newAudio.play().catch(err => {
            console.error('Playback error:', err);
          });
        }
      })
      .catch(err => {
        if (err?.status == 401) {
          toast.warning(err.data.message || err.message);
        } else {
          toast.error(err.data.message || err.message);
        }
      });
  };
  useEffect(() => {
    return () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, [audio]);

  useEffect(() => {
    if (!watch('voice') && templateList?.length > 0) {
      // Set the first voice as default if no voice is selected
      const firstVoice = templateList[0];
      setValue('voice', firstVoice.voice);
      dispatch(
        handleUpdateAppearanceItem({
          key: 'voice',
          value: firstVoice.voice,
        })
      );
    }
  }, [watch('voiceResponse'), watch('voice'), templateList]);

  useEffect(() => {
    const isCustom = !!watch('style') || !!watch('speed') || !!watch('emotion') || !!watch('emphasis') || !!watch('pauses');

    setConfigType(isCustom ? 'custom' : 'template');
  }, []);

  if (isLoading) {
    return <LoadingComponent />;
  }

  return (
    <>
      {appearanceState?.bot_code !== BotTypeEnum.CALLBOT && (
        <Form.Group className="tw-w-full tw-flex tw-items-center tw-gap-2">
          <Form.Check
            disabled={!customizationAllowed}
            type="switch"
            id="voiceResponse"
            className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
            label={t('DashboardSettingCustomizeChat/allow-receive-voice')}
            isInvalid={!!errors.voiceResponse}
            feedbackType="invalid"
            feedback={errors.voiceResponse?.message as string}
            {...register('voiceResponse', { onChange: handleToggleChange })}
          />
          {!customizationAllowed && <ProBadge className="tw-ms-2" onClose={() => dispatch(handleResetAppearance())} />}
          <PopoverWithAudio data={voiceCommandPopData} />
        </Form.Group>
      )}

      {voiceResponseEnabled && (
        <>
          <div className="tw-w-full  tw-justify-start tw-items-center tw-inline-flex">
            <PSInput
              value={inputValue}
              onChange={e => {
                setInputValue(e.target.value);
              }}
              type="text"
              max={100}
              maxLength={100}
              disabled={!permissions.settingsPage.widget.customization.advancedVoiceSettings}
              className="tw-text-gray-800 tw-text-input  tw-placeholder:text-gray-400  tw-text-[16px] tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-border-solid tw-h-10 tw-px-4 tw-py-2.5 tw-bg-white tw-rounded-md tw-shadow-sm tw-border tw-border-gray-300 tw-justify-start tw-items-center tw-inline-flex tw-mr-4"
            />

            <div
              role="button"
              tabIndex={0}
              className="tw-cursor-pointer"
              onClick={handleVoiceResponseSound}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') handleVoiceResponseSound();
              }}
            >
              {isProcessing ? (
                <div className="tw-w-6 tw-h-6 ">
                  <Spinner animation="border" variant="success" size="sm" />
                </div>
              ) : (
                <BiSolidVolumeFull className="tw-w-6 tw-h-6 tw-text-blue-500 " />
              )}
            </div>
          </div>

          <Form.Group className="tw-w-full tw-flex tw-items-center tw-gap-5 tw-block">
            <DropDownWithImage
              isAdvanced={permissions.settingsPage.widget.customization.advancedVoiceSettings}
              list={templateList}
              select={watch('voice') ?? undefined}
              onSelect={handleVoiceSelect}
            />
          </Form.Group>

          <div className="tw-w-full lg:tw-h-10 tw-h-full tw-p-0.5 tw-bg-gray-100 tw-rounded-full tw-justify-center tw-items-center tw-gap-0.5 tw-flex">
            <ToggleButton type="template" currentType={configType} onClick={setConfigType} label={t('DashboardSettingCustomizeChat/l-21')} />
            <ToggleButton type="custom" currentType={configType} onClick={setConfigType} label={t('DashboardSettingCustomizeChat/l-22')} />
          </div>

          <div className="tw-w-full">
            {configType == 'template' && (
              <div className="tw-w-full tw-col-span-12">
                <Form.Group>
                  <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                    {t('DashboardSettingCustomizeChat/l-21')}
                    {!permissions.settingsPage.widget.customization.advancedVoiceSettings && <ProBadge className="tw-ms-2" />}
                  </Form.Label>
                  <Form.Select
                    translate="no"
                    disabled={!permissions.settingsPage.widget.customization.advancedVoiceSettings}
                    isInvalid={!!errors.position}
                    {...register('template', {
                      onChange(event) {
                        setValue('template', event.target.value);
                        dispatch(
                          handleUpdateAppearanceItem({
                            key: 'template',
                            value: event.target.value,
                          })
                        );
                        customList.map(item => {
                          setValue(item, '');
                          dispatch(
                            handleUpdateAppearanceItem({
                              key: item,
                              value: '',
                            })
                          );
                        });
                      },
                    })}
                  >
                    <option value="">{t('NodeParentChanger/key3')}</option>
                    {data?.allowed_voice_template?.map(item => {
                      return (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      );
                    })}
                  </Form.Select>
                  {/* Validation */}
                  {errors.template && <span className="tw-text-red-500 tw-text-sm tw-block">{errors.template?.message}</span>}
                </Form.Group>
              </div>
            )}

            {configType == 'custom' && (
              <>
                {customList.map(key => (
                  <VoiceSettingSelect
                    key={key}
                    name={key}
                    label={t(`DashboardSettingCustomizeChat/l-${key}`)}
                    options={data?.allowed_voice_setting?.[key] ?? []}
                    disabled={!permissions.settingsPage.widget.customization.advancedVoiceSettings}
                    register={register}
                    errors={errors}
                    onChange={event => {
                      setValue(key, event.target.value);
                      dispatch(
                        handleUpdateAppearanceItem({
                          key,
                          value: event.target.value,
                        })
                      );

                      setValue('template', '');
                      dispatch(
                        handleUpdateAppearanceItem({
                          key: 'template',
                          value: '',
                        })
                      );
                    }}
                    showProBadge={!permissions.settingsPage.widget.customization.advancedVoiceSettings}
                    chooseLabel={t('NodeParentChanger/key3')}
                  />
                ))}
              </>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default WrappedErrorBoundary(AppearanceVoiceResponseToggle);

const VoiceSettingSelect = ({
  label,
  name,
  options = [],
  disabled,
  register,
  errors,
  onChange,
  showProBadge = false,
  chooseLabel,
}: {
  label: string;
  name: string;
  options: [];
  disabled: boolean;
  register: any;
  errors: any;
  onChange: (event: any) => void;
  showProBadge: boolean;
  chooseLabel: string;
}) => (
  <div className="tw-w-full tw-col-span-12">
    <Form.Group>
      <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
        {label}
        {showProBadge && <ProBadge className="tw-ms-2" />}
      </Form.Label>
      <Form.Select
        translate="no"
        disabled={disabled}
        isInvalid={!!errors[name]}
        {...register(name, {
          onChange,
        })}
      >
        <option value="">{chooseLabel}</option>
        {options.map(item => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
      </Form.Select>
      {errors[name] && <span className="tw-text-red-500 tw-text-sm tw-block">{errors[name]?.message}</span>}
    </Form.Group>
  </div>
);

const ToggleButton = ({
  type,
  currentType,
  onClick,
  label,
}: {
  type: ConfigType;
  currentType: ConfigType;
  onClick: (type: ConfigType) => void;
  label: string;
}) => (
  <div
    role="button"
    tabIndex={0}
    onClick={() => onClick(type)}
    onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        onClick(type);
      }
    }}
    className={`tw-cursor-pointer tw-grow tw-shrink tw-basis-0 tw-h-9 tw-px-6 tw-py-2 ${
      currentType === type ? 'tw-bg-white tw-shadow-sm' : 'tw-bg-gray-100'
    } tw-rounded-full tw-border tw-border-gray-200/10 tw-justify-center tw-items-center tw-flex tw-overflow-hidden`}
  >
    <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-leading-tight">{label}</div>
  </div>
);
